/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-12 15:40:50                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-25 16:02:48                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * DetailsDrawer: display details drawer
*/

/* Imports */
import { Button, Card, CardBody, CardFooter, Typography } from "@material-tailwind/react";
import { Modal } from "@mui/material";
import { useState } from "react";
import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
/***/

/* Components */
import TechnicianSelect from "../../../components/data/technician/TechnicianSelect";
import DateRangeSelect from "../../../components/data/date-range/DateRangeSelect";
/***/

/* Services */
import { api } from "../../../services/api/api";
/***/

import "./PlanningModal.scss";
import TimeTypeSelect from "../../../components/data/time-type/TimeTypeSelect";
import { useNavigate } from "react-router-dom";
import { showApiErrorToast } from "../../../services/api/error";
import Loading from "../../../components/loading/Loading";

/**
* DetailsDrawer: display details drawer
* @param time - time to display
* @param onUpdate - on update callback, return updated time
*/
export default function TimeCreateModal({
  open,
  times,
  onClose,
  onTimeCreate
}: {
  open: boolean,
  times: { start: Date, end: Date },
  onClose: () => void,
  onTimeCreate: (time: EasyTech.Time) => void
}) {
  const { t } = useTranslation();
  const [onEdit, setOnEdit] = useState<string | null>(null);
  const [tech, setTech] = useState<EasyTech.Technician | null>(null);
  const [timeType, setTimeType] = useState<EasyTech.TimeType | null>(null);
  const [scheduleState, setSchedule] = useState<{ start: string, end: string | null }>({ start: times.start.toUTCString(), end: times.end.toUTCString() });
  const [loadingState, setLoadingState] = useState<boolean>(false);

  const navigate = useNavigate();

  /**
  * save: Save
  */
  const save = (): void => {
    if (!onEdit || !tech || !timeType) return;
    setLoadingState(true);
    api.post("admin/technician_times", {
      technician: { id: tech.id },
      type: { id: timeType.id },
      date_from: scheduleState.start,
      date_to: scheduleState.end || times.end.toUTCString()
    }).then((ret) => {
      if (onTimeCreate)
        onTimeCreate({ ...ret as EasyTech.Time, technician: tech });
      toast.success(t("planification:notifications:planTechnician:success"));
    }).catch((err) => {
      console.error(err);
      showApiErrorToast(err);
    }).finally(() => {
      setLoadingState(false);
      onClose();
    });
  };
  /***/

  const goToCreateWorkorder = (() => {
    const query = "?start=" + times.start.toISOString() + "&end=" + times.end.toISOString();
    navigate("/create-workorder" + query);
    return;
  });

  return (

    <Modal
      open={open}
      onClose={onClose}
      className="te-modal"
      aria-labelledby="te-modal"
      aria-describedby="te-modal">
      <Card className="te-modal-content  rounded-lg">
        <button className="close-button" disabled={loadingState} onClick={() => onClose()} >
          <XMarkIcon className="w-4" />
        </button>
        <CardBody>
          <div className="flex items-center gap-3 mb-5">
            <div className="rounded-full border p-2 bg-light-green-200">
              <CalendarIcon className="w-7 text-green-700" />
            </div>
            <Typography variant="h5">
              {t("planification:createEvent")}
            </Typography>
          </div>

          <div className="py-2">
            <DateRangeSelect range={scheduleState || { start: times.start.toUTCString(), end: times.end.toUTCString() }}
              onChange={el => setSchedule(el)} hideDuration />
          </div>

          {onEdit === "time" && <>
            <div className="py-4">
              <TimeTypeSelect value={timeType || undefined} onChange={(el) => setTimeType(el)} />
            </div>
            <div className="py-4">
              <TechnicianSelect value={tech || undefined} onChange={(el) => setTech(el)} />
            </div>
          </>}


        </CardBody>
        <CardFooter className="pt-0 flex gap-2 justify-end">
          {!onEdit ?
            <>
              <Button size="sm" variant="outlined" onClick={onClose}>
                {t("planification:cancel")}
              </Button>
              <Button color="blue"
                onClick={() => setOnEdit("time")}>
                {t("planification:unavailability")}
              </Button>

              <Button color="blue"
                onClick={() => goToCreateWorkorder()}>
                {t("planification:event:workorder:title")}
              </Button>
            </>
            :
            <>
              <Button variant="outlined"
                disabled={loadingState}
                onClick={() => setOnEdit(null)}>
                {t("planification:cancel")}
              </Button>

              <Button color="blue"
                disabled={loadingState}
                onClick={save}>
                {loadingState ?
                  <Loading size="h-4 w-4" />
                  :
                  t("planification:save")
                }
              </Button>
            </>
          }
        </CardFooter>
      </Card>
    </Modal>
  );
}
/***/
